import {
  Order,
  OrderItem,
  Product,
  ProductCategoryEnum,
  ProductVariant,
  useProfileCustomerSubscriptionQuery,
  useStoreQuery,
} from '#apollo/schema';
import {Colors} from '#theme';
import {addDays, formattedDate} from '#util';
import {ProductTid} from '#util/subscriptions';

import {getValues} from './CTASubscription.util';

import {SubscriptionStatus, UpcomingOrderType} from './CTASubscription.types';

const filterProductVariants = (
  productVariants: ProductVariant[],
  category: ProductCategoryEnum,
): string[] =>
  productVariants?.filter(item => item?.product?.category === category)?.map(item => item?.id) ||
  [];

export const getEarliestBillingDate = (
  orders: Order[],
  matchesItem: (item: OrderItem) => boolean,
) =>
  orders?.reduce((prev, curr) => {
    if (!curr || !curr.orderItems || !curr.billingDate) return prev;

    const currBillingDate = new Date(curr.billingDate);
    if (isNaN(currBillingDate.getTime())) return prev;

    // Only consider orders that contain a matching item; otherwise the accumulator must never be
    // seeded or updated by them (previously the `!prev` branch seeded from the first order of any
    // kind, which could surface a non-matching order's date).
    if (!curr.orderItems.some(matchesItem)) return prev;

    if (!prev || currBillingDate < new Date(prev)) return curr.billingDate;

    return prev;
  }, null);

export const useCTACustomerState = () => {
  const {data, loading} = useProfileCustomerSubscriptionQuery();
  const {me} = data || {};
  const {customerAccount} = me || {};
  const {subscription, upcomingOrder, orders} = customerAccount || {};
  const {status, pausedProductVariantIds, hasMembership} = subscription || {};
  const {data: dataStore, loading: loadingStore} = useStoreQuery();
  const {store} = dataStore || {};
  const {productVariants} = store || {};

  const membershipProductVariantIds = filterProductVariants(
    productVariants as ProductVariant[],
    ProductCategoryEnum.Membership,
  );
  const supplementProductVariantsIds = filterProductVariants(
    productVariants as ProductVariant[],
    ProductCategoryEnum.Supplement,
  );

  const earliestBillingDateMem = getEarliestBillingDate(
    orders as Order[],
    item => !!item.isMembership,
  );
  const earliestBillingDateSub = getEarliestBillingDate(orders as Order[], item =>
    supplementProductVariantsIds.includes(String(item.id)),
  );

  const prevOrder = orders?.reduce<UpcomingOrderType | null>((prev, curr) => {
    if (!prev) return curr;
    if (prev && curr.billingDate > prev.billingDate) return curr;
    return prev;
  }, null);
  const prevOrderDatePlus30 = addDays(prevOrder?.billingDate, 30);
  const prevOrderDatePlus90 = addDays(prevOrder?.billingDate, 90);

  const earliestBillingDateMemFormatted = earliestBillingDateMem
    ? formattedDate(earliestBillingDateMem)
    : null;
  const earliestBillingDateSubFormatted = earliestBillingDateSub
    ? formattedDate(earliestBillingDateSub)
    : null;

  const subscriptionStatus = (status || 'ACTIVE').toUpperCase() as SubscriptionStatus;
  const canceledOrExpired = ['CANCELLED', 'EXPIRED'].includes(subscriptionStatus.toUpperCase());

  const pausedSupplementProducts =
    pausedProductVariantIds
      ?.filter(id => supplementProductVariantsIds.includes(id))
      .map(id => productVariants?.find(variant => variant.id === id)?.product)
      .filter((product): product is Product => product !== undefined) || [];

  const activeSupplementProducts =
    subscription?.items
      ?.filter(item => supplementProductVariantsIds.includes(item.variantId))
      .map(item => productVariants?.find(variant => variant.id === item.variantId)?.product)
      .filter((product): product is Product => product !== undefined) || [];

  const allSupplementProducts: ProductTid[] = Array.from(
    new Set([...pausedSupplementProducts, ...activeSupplementProducts].map(variant => variant.id)),
  ).map(id => {
    const variant = [...pausedSupplementProducts, ...activeSupplementProducts].find(
      v => v.id === id,
    );
    return {
      id: variant?.id ?? '',
      tid: variant?.tid || '',
      displayTheme: variant?.displayTheme || '',
    };
  });

  const {
    displayStatus,
    heading,
    subHeading,
    subHeadingMobile,
    bgImage,
    bgImageTablet,
    bgImageMobile,
    bgMain,
    bgTablet,
    bgMobile,
    textColor,
    textColorSubscriptionStatus,
    textSinceColor,
    headingSecond,
    subheadingSecond,
    textColorSecond,
    textColorSubHeadingSecond,
    secondBtnText,
    secondBtnLink,
    bgUpSell,
    textSince,
    secondBtnVariant,
    skipBtnVariant,
    align,
  } = getValues({
    status,
    earliestBillingDateMemFormatted,
    earliestBillingDateSubFormatted,
    subscription,
    hasMembership: hasMembership ?? false,
    canceledOrExpired,
    membershipProductVariantIds: membershipProductVariantIds || [],
    activeSupplementProducts: activeSupplementProducts || [],
    prevOrderDatePlus30,
    prevOrderDatePlus90,
    productVariants: productVariants || [],
    allSupplementProducts,
  });

  return {
    heading,
    textColor: textColor as Colors,
    textColorSubscriptionStatus: textColorSubscriptionStatus as Colors,
    textSinceColor: textSinceColor as Colors,
    headingSecond,
    subheadingSecond,
    textColorSecond: textColorSecond as Colors,
    textColorSubHeadingSecond: textColorSubHeadingSecond as Colors,
    secondBtnText,
    secondBtnLink,
    bgMain,
    bgTablet,
    bgMobile,
    bgUpSell,
    status: displayStatus,
    subscription,
    loading: loading || loadingStore,
    earliestBillingDate: earliestBillingDateMemFormatted,
    textSince,
    hasMembership,
    canceledOrExpired,
    secondBtnVariant,
    upcomingOrder,
    productVariants,
    skipBtnVariant,
    bgImage,
    bgImageTablet,
    bgImageMobile,
    subHeading,
    subHeadingMobile,
    align,
  };
};
